
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Header, Footer } from '../components';
import { Icon } from '../components/ui/Icon';
import coffeeHeroBg from '../assets/coffee_hero_bg.png';

export default function CoffeePage() {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const staggerContainer = {
        animate: { transition: { staggerChildren: 0.1 } }
    };

    return (
        <div ref={containerRef} className="min-h-screen bg-stone-50 font-sans selection:bg-orange-500 selection:text-white">
            <Header />
            
            {/* Hero Section */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                <motion.div 
                    className="absolute inset-0 z-0"
                    style={{ y: heroY, opacity: heroOpacity }}
                >
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: `url(${coffeeHeroBg})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-stone-900/90" />
                </motion.div>

                <div className="relative z-10 container mx-auto px-6 text-center text-white mt-16">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={staggerContainer}
                    >
                        <motion.span 
                            variants={fadeInUp}
                            className="inline-block px-4 py-1.5 mb-6 text-sm font-medium tracking-widest uppercase border border-white/20 rounded-full bg-white/5 backdrop-blur-sm text-orange-400"
                        >
                            The Origin of Flavor
                        </motion.span>
                        <motion.h1 
                            variants={fadeInUp}
                            className="text-6xl md:text-8xl font-bold mb-8 tracking-tight leading-none"
                        >
                            Ethiopian <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-600">
                                Coffee
                            </span>
                        </motion.h1>
                        <motion.p 
                            variants={fadeInUp}
                            className="text-xl md:text-2xl font-light text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12"
                        >
                            A heritage of excellence cultivated from the birthplace of coffee to your cup.
                        </motion.p>
                    </motion.div>
                </div>

                <motion.div 
                    className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 flex flex-col items-center gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 1 }}
                >
                    <span className="text-xs uppercase tracking-widest">Scroll</span>
                    <div className="w-[1px] h-12 bg-gradient-to-b from-white to-transparent" />
                </motion.div>
            </section>

            {/* Introduction Section */}
            <section className="py-32 bg-stone-50 overflow-hidden">
                <div className="container mx-auto px-6">
                    <div className="flex flex-col lg:flex-row items-center gap-20">
                        <motion.div 
                            className="lg:w-1/2"
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <span className="text-orange-600 font-bold tracking-widest uppercase text-sm mb-4 block">
                                Our Passion
                            </span>
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                                More than a beverage, <br/>
                                <span className="italic text-stone-600">it's a culture.</span>
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed mb-6">
                                In Ethiopia, coffee is an inseparable part of daily life. As the birthplace of coffee, our nation's history is steeped in the rich aroma of roasted beans. It is a symbol of hospitality, community, and tradition.
                            </p>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                Beyond its cultural significance, coffee is a cornerstone of the economy, providing livelihoods for over 15 million people and connecting us to the world through taste.
                            </p>
                        </motion.div>

                        <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {[
                                { label: 'Wet Mill Stations', value: '22', icon: 'home', color: 'bg-orange-500' },
                                { label: 'Farmers Network', value: '22,600+', icon: 'heart', color: 'bg-rose-500' },
                                { label: 'Specialty Stations', value: '7', icon: 'star', color: 'bg-amber-500' },
                                { label: 'Global Market Share', value: '3%', icon: 'globe', color: 'bg-blue-500' },
                            ].map((stat, index) => (
                                <motion.div
                                    key={stat.label}
                                    className="p-8 rounded-3xl bg-white shadow-xl shadow-stone-200/50 border border-stone-100 relative group overflow-hidden"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1, duration: 0.6 }}
                                    whileHover={{ y: -5 }}
                                >
                                    <div className={`absolute top-0 right-0 w-24 h-24 ${stat.color} opacity-[0.03] rounded-bl-full group-hover:scale-150 transition-transform duration-500`} />
                                    
                                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${stat.color} bg-opacity-10 text-opacity-100 ${stat.color.replace('bg-', 'text-')} mb-6`}>
                                        <Icon name={stat.icon as any} className="w-6 h-6" />
                                    </div>
                                    <div className="text-4xl font-bold text-gray-900 mb-2 tracking-tight">{stat.value}</div>
                                    <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">{stat.label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Production & Infrastructure */}
            <section className="py-32 bg-[#1c1917] text-white relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-[0.02]">
                     <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#grid)" />
                    </svg>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="flex flex-col md:flex-row items-center gap-16">
                         <div className="w-full md:w-1/2">
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                            >
                                <span className="text-orange-500 font-bold tracking-widest uppercase text-sm mb-4 block">
                                    Infrastructure
                                </span>
                                <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
                                    Built for <span className="text-orange-500">Quality</span> <br/>
                                    at Scale
                                </h2>
                                <div className="space-y-8 text-lg text-stone-300">
                                    <p>
                                        We operate <span className="text-white font-semibold">22 wet mill stations</span> strategically located in Ethiopia's prime coffee-producing regions. These stations are situated at optimum altitudes to ensure the highest quality cherry development.
                                    </p>
                                    <p>
                                        Our modern processing facilities combine traditional wisdom with innovative technology. We have established a robust network with over 22,600 farmers who supply us with premium red cherries, ensuring a consistent and sustainable supply chain.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                        
                        <div className="w-full md:w-1/2">
                            <motion.div
                                className="bg-white/5 backdrop-blur-md p-10 rounded-3xl border border-white/10"
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                            >
                                <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white">
                                    <Icon name="check-circle" className="text-green-500 w-8 h-8" />
                                    Global Certifications
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {['Organic Certified', 'Rainforest Alliance', 'Fair Trade', 'UTZ Certified', 'Cafe Practice'].map((cert, i) => (
                                        <motion.div 
                                            key={cert} 
                                            className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5"
                                            initial={{ opacity: 0, y: 10 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: i * 0.1 }}
                                        >
                                            <div className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]" />
                                            <span className="font-medium text-stone-200">{cert}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Process & Origins */}
            <section className="py-32 bg-orange-50 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-200 to-transparent" />
                
                <div className="container mx-auto px-6 relative">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="text-orange-600 font-bold tracking-widest uppercase text-sm mb-4 block">
                            Our Process
                        </span>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Origins & Precision</h2>
                        <p className="text-xl text-gray-600">
                            From the renowned Sidama region to your cup, every step is monitored for perfection.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* The Process */}
                        <motion.div
                            className="bg-white p-10 rounded-[2rem] shadow-xl shadow-orange-100/50 border border-orange-100/50"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10, transition: { duration: 0.3 } }}
                        >
                            <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center text-white mb-8 shadow-lg shadow-orange-500/30">
                                <Icon name="zap" className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Precision Processing</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Our wet mill stations are equipped with state-of-the-art machinery. We employ rigorous standards, including manual hand-picking to remove any imperfect beans, ensuring only the finest quality makes it to processing.
                            </p>
                        </motion.div>

                        {/* Quality Control */}
                        <motion.div
                            className="bg-white p-10 rounded-[2rem] shadow-xl shadow-orange-100/50 border border-orange-100/50"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            whileHover={{ y: -10, transition: { duration: 0.3 } }}
                        >
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mb-8 shadow-lg shadow-blue-500/30">
                                <Icon name="award" className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Cup Testing</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Every batch undergoes rigorous "cup testing" in our dedicated liquoring unit. This allows us to determine precise flavor profiles and match them with our customers' specific preferences.
                            </p>
                        </motion.div>

                        {/* Key Locations */}
                        <motion.div
                            className="bg-white p-10 rounded-[2rem] shadow-xl shadow-orange-100/50 border border-orange-100/50"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            whileHover={{ y: -10, transition: { duration: 0.3 } }}
                        >
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-600 rounded-2xl flex items-center justify-center text-white mb-8 shadow-lg shadow-green-500/30">
                                <Icon name="mountain" className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Prime Locations</h3>
                            <ul className="space-y-4 text-gray-600">
                                <li className="flex items-start gap-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5" />
                                    <span><strong>Sidama Region:</strong><br/> Chiri, Bensa, Bona Zuriya</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5" />
                                    <span><strong>Facilities:</strong><br/> Goda Yamasira, Hagire Buticha, Michael Girma Wet Mill</span>
                                </li>
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>
            
            <Footer />
        </div>
    );
}

